import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import { colors } from '../theme';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import KeyIcon from '@mui/icons-material/Key';
import HandshakeIcon from '@mui/icons-material/Handshake';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';

function FinancialSection() {
  const plans = [
    {
      name: 'Outright Purchase',
      icon: <ShoppingCartIcon sx={{ fontSize: 50 }} />,
      description: 'Own your charging infrastructure from day one',
      color: colors.green,
      features: [
        'Full ownership from purchase',
        'No monthly payments',
        'Maximum long-term savings',
        'Asset on your balance sheet',
        'Immediate ROI benefits',
        'Full control over operations',
      ],
      ideal: 'Organizations with capital budget',
      payment: 'One-time payment',
    },
    {
      name: 'Lease-to-Own',
      icon: <KeyIcon sx={{ fontSize: 50 }} />,
      description: 'Spread costs with flexible payment terms',
      color: colors.yellow,
      features: [
        '💰 $0 upfront investment',
        'Shorter contract period',
        'Ownership after lease term',
        'Maintenance free during contract',
        'Flexible charger rate',
      ],
      ideal: 'Businesses wanting asset ownership after lease',
      payment: 'Zero upfront cost',
      highlight: 'Zero Upfront Cost',
    },
    {
      name: 'Profit Sharing',
      icon: <HandshakeIcon sx={{ fontSize: 50 }} />,
      description: 'Zero payment option - we share the revenue',
      color: colors.green,
      features: [
        '💰 $0 upfront payment',
        'No installation costs',
        'We own & maintain equipment',
        'Revenue split arrangement',
        'No financial risk',
        'Professional management',
      ],
      ideal: 'Commercial & Residential',
      payment: 'Zero upfront cost',
      highlight: 'Zero Payment Option',
      popular: true,
    },
  ];

  return (
    <Box
      id="financial"
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: colors.white,
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 800,
              mb: 2,
              color: colors.black,
            }}
          >
            Financial{' '}
            <Box component="span" sx={{ color: colors.yellow }}>
              Models
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#666',
              maxWidth: '700px',
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.2rem' },
            }}
          >
            Flexible payment options to suit your business needs
          </Typography>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={4}>
          {plans.map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  position: 'relative',
                  border: plan.popular
                    ? `3px solid ${plan.color}`
                    : '1px solid #e0e0e0',
                  transition: 'all 0.3s ease',
                  overflow: 'visible',
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: `0 16px 48px ${plan.color}30`,
                  },
                }}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -35,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: colors.yellow,
                      color: colors.black,
                      px: 3,
                      py: 0.6,
                      borderRadius: 3,
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      boxShadow: `0 4px 12px ${colors.yellow}60`,
                      zIndex: 3,
                    }}
                  >
                    <StarIcon sx={{ fontSize: 16 }} />
                    POPULAR
                  </Box>
                )}

                {/* Highlight Badge */}
                {plan.highlight && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: colors.green,
                      color: colors.white,
                      px: 4,
                      py: 0.8,
                      borderRadius: 3,
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      boxShadow: `0 4px 12px ${colors.green}60`,
                      zIndex: 2,
                    }}
                  >
                    {plan.highlight}
                  </Box>
                )}

                <CardContent sx={{ p: 4, pt: plan.popular || plan.highlight ? 5 : 4 }}>
                  {/* Icon */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: `${plan.color}20`,
                      color: plan.color === colors.yellow ? colors.black : plan.color,
                      mb: 3,
                    }}
                  >
                    {plan.icon}
                  </Box>

                  {/* Plan Name */}
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: colors.black,
                      fontSize: { xs: '1.5rem', md: '1.75rem' },
                    }}
                  >
                    {plan.name}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#666',
                      mb: 3,
                      lineHeight: 1.7,
                      minHeight: 48,
                    }}
                  >
                    {plan.description}
                  </Typography>

                  {/* Payment Type */}
                  <Chip
                    label={plan.payment}
                    sx={{
                      bgcolor: `${plan.color}20`,
                      color: plan.color === colors.yellow ? colors.black : plan.color,
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      mb: 3,
                      px: 1,
                    }}
                  />

                  {/* Features */}
                  <List sx={{ py: 0, mb: 3 }}>
                    {plan.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 35 }}>
                          <CheckCircleIcon
                            sx={{
                              color: plan.color,
                              fontSize: 20,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            fontSize: '0.95rem',
                            color: '#444',
                            fontWeight: feature.includes('$0') ? 700 : 400,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  {/* Ideal For */}
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor:
                        plan.color === colors.yellow
                          ? `${colors.yellow}20`
                          : `${colors.green}15`,
                      border: `2px solid ${plan.color}40`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#666',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        mb: 0.5,
                      }}
                    >
                      IDEAL FOR:
                    </Typography>
                    <Typography
                      sx={{
                        color: colors.black,
                        fontWeight: 700,
                        fontSize: '0.95rem',
                      }}
                    >
                      {plan.ideal}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Additional Info */}
        <Box
          sx={{
            mt: 8,
            p: 4,
            borderRadius: 4,
            bgcolor: colors.white,
            border: `2px solid ${colors.green}40`,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: colors.black,
            }}
          >
            Need Help Choosing?
          </Typography>
          <Typography
            sx={{
              color: '#666',
              mb: 3,
              fontSize: '1rem',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Our team can help you analyze your requirements and recommend the best
            financial model for your specific situation.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Chip
              label="ROI Calculator"
              sx={{
                bgcolor: `${colors.green}15`,
                color: colors.green,
                fontWeight: 600,
                px: 1,
              }}
            />
            <Chip
              label="Free Consultation"
              sx={{
                bgcolor: `${colors.yellow}40`,
                color: colors.black,
                fontWeight: 600,
                px: 1,
              }}
            />
            <Chip
              label="Customizable Terms"
              sx={{
                bgcolor: `${colors.green}15`,
                color: colors.green,
                fontWeight: 600,
                px: 1,
              }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default FinancialSection;
